// libs/utils/src/subscription-state.ts
import type { SubscriptionTier } from "./plans";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The subset of `Subscription` this needs — shaped so both the server
 * (Prisma row) and the client (JSON over the wire, dates as ISO strings) can
 * pass their row straight in.
 */
export type SubscriptionStateInput = {
  tier?: string | null;
  status?: string | null;
  lsCurrentPeriodEnd?: string | Date | null;
  /** Present only for a real Lemon Squeezy subscription; null for the in-house trial. */
  lsSubscriptionId?: string | number | null;
};

export type ResolvedEntitlement = {
  /** The tier that should actually gate features RIGHT NOW. */
  effectiveTier: SubscriptionTier;
  /** True while a trial (in-house or LS-owned) is ongoing. */
  isTrialing: boolean;
  /** True once an in-house trial's `lsCurrentPeriodEnd` has passed. */
  trialExpired: boolean;
  /** Whole days left in an active trial (ceil, min 1). `null` when not trialing, or when trialing with no known end date. */
  daysLeft: number | null;
};

function normalizeTier(tier: string | null | undefined): SubscriptionTier {
  return tier === "PRO" || tier === "ULTIMATE" ? tier : "FREE";
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Whole days left until `endsAt`, ceiled and floored at 1 (never "0 days left" while still active). */
function daysUntil(endsAt: Date, now: Date): number {
  return Math.max(1, Math.ceil((endsAt.getTime() - now.getTime()) / DAY_MS));
}

const noTrial = (effectiveTier: SubscriptionTier): ResolvedEntitlement => ({
  effectiveTier,
  isTrialing: false,
  trialExpired: false,
  daysLeft: null,
});

/**
 * Decides entitlement FROM DATA — one rule table, used by every caller
 * (server guards, usage limits, client paywall copy) instead of each site
 * reading `tier`/`status` off the DB row with its own ad-hoc pre-gate. A
 * Codex review (2026-09-21) found five server sites and one client site each
 * handling CANCELED-in-grace and PAST_DUE slightly differently — this
 * function is now the single decision every one of them calls.
 *
 * Never writes anything. `TrialService.expireFinishedTrials`
 * (apps/server/src/billing/trial.service.ts) remains the sole writer of the
 * real DB downgrade + the "trial ended" email; this is a pure read-side
 * clamp that closes the gap between an event happening (a trial's
 * `lsCurrentPeriodEnd` passing, a payment failing) and the write that
 * eventually catches up (the once-daily 06:00 UTC cron, or an LS webhook).
 *
 * ## Rule table (checked in this order — `status` first, then ownership)
 *
 * | status                        | `lsSubscriptionId` | condition                    | `effectiveTier` |
 * |-------------------------------|---------------------|-------------------------------|------------------|
 * | `ACTIVE`                      | any                  | —                              | `tier`           |
 * | `TRIALING`                    | set (LS card trial)  | —                              | `tier` (LS-owned — never downgraded here, regardless of `lsCurrentPeriodEnd`) |
 * | `TRIALING`                    | absent (in-house)    | `lsCurrentPeriodEnd` missing   | `tier` (data gap treated as active, not punished) |
 * | `TRIALING`                    | absent (in-house)    | `lsCurrentPeriodEnd` < now     | `FREE` (expired, cron hasn't swept it yet) |
 * | `TRIALING`                    | absent (in-house)    | `lsCurrentPeriodEnd` >= now    | `tier`           |
 * | `CANCELED`                    | set                  | `lsCurrentPeriodEnd` > now     | `tier` (paid grace — LS already billed this period) |
 * | `CANCELED`                    | anything else        | —                              | `FREE`           |
 * | `PAST_DUE`                    | any                  | —                              | `FREE`           |
 * | anything else (`INCOMPLETE`, unknown, missing, no subscription row) | any | — | `FREE` |
 *
 * The boundary is always strict: `lsCurrentPeriodEnd < now` is expired,
 * `lsCurrentPeriodEnd > now` is the only case that grants paid grace — a
 * period end exactly equal to `now` grants neither (an in-house trial at
 * that instant is still `isTrialing`, per the `<` check; a CANCELED grace
 * period at that instant is already `FREE`, per the `>` check). Client
 * offer/paywall copy aligns to this same boundary.
 */
export function resolveEntitlement(
  subscription: SubscriptionStateInput | null | undefined,
  now: Date = new Date(),
): ResolvedEntitlement {
  const tier = normalizeTier(subscription?.tier);
  const status = subscription?.status ?? null;
  const hasLsSubscription = Boolean(subscription?.lsSubscriptionId);
  const endsAt = toDate(subscription?.lsCurrentPeriodEnd);

  switch (status) {
    case "ACTIVE": {
      // A real, currently-billing subscription. LS owns this status
      // transition end-to-end — nothing here to clamp.
      return noTrial(tier);
    }

    case "TRIALING": {
      if (hasLsSubscription) {
        // A Lemon Squeezy card trial: LS's own webhooks own its expiry, not
        // the in-house cron, so it is never downgraded here regardless of
        // `lsCurrentPeriodEnd`. Still surfaced as `isTrialing` (it IS one,
        // from the user's perspective) with an informational `daysLeft` when
        // the date is known.
        return {
          effectiveTier: tier,
          isTrialing: true,
          trialExpired: false,
          // Only surface a countdown when the date is actually still ahead —
          // clamping a stale/past LS date to "1 day left" (the in-house
          // `daysUntil` floor) would misreport a trial LS should have
          // already transitioned out of.
          daysLeft: endsAt && endsAt.getTime() > now.getTime() ? daysUntil(endsAt, now) : null,
        };
      }
      // In-house 14-day trial — the one case this helper is allowed to
      // downgrade ahead of `TrialService.expireFinishedTrials`.
      if (!endsAt) {
        return { effectiveTier: tier, isTrialing: true, trialExpired: false, daysLeft: null };
      }
      if (endsAt.getTime() < now.getTime()) {
        return { effectiveTier: "FREE", isTrialing: false, trialExpired: true, daysLeft: 0 };
      }
      return {
        effectiveTier: tier,
        isTrialing: true,
        trialExpired: false,
        daysLeft: daysUntil(endsAt, now),
      };
    }

    case "CANCELED": {
      // Paid grace: LS already billed for a period that has not ended yet
      // (auto-renew turned off, access continues until `lsCurrentPeriodEnd`).
      // Anything else — no LS subscription backing this row, or the grace
      // period has itself lapsed — is FREE. Never "isTrialing": this is a
      // lapsing purchase, not a trial.
      if (hasLsSubscription && endsAt && endsAt.getTime() > now.getTime()) {
        return noTrial(tier);
      }
      return noTrial("FREE");
    }

    default: {
      // PAST_DUE, INCOMPLETE, an unrecognized/missing status, or no
      // subscription row at all — none of these grant access. A failed
      // payment does not entitle; LS re-grants by transitioning to ACTIVE,
      // not by us inferring good faith from a stale `tier` column.
      return noTrial("FREE");
    }
  }
}
